import { useCallback, useEffect, useRef, useState } from 'react'
import { Pause, Play, SkipForward, UserRound } from 'lucide-react'

import {
  useAppendDiscourseTurn,
  useDiscourseSession,
  useSimulateDiscourseTurn,
} from '@/hooks/use-discourse-classifiers'
import { useDiscourseSessionSse } from '@/hooks/use-discourse-session-sse'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type DiscourseDiscussionPanelProps = {
  sessionId: string
  profileId: string
  /** Role label for the human/demo protagonist (e.g. interviewer, patient). */
  protagonistRole: string
  /** Role label for the simulated counterpart (e.g. candidate, practitioner). */
  counterpartRole: string
  title?: string
  subtitle?: string
  contextLabel?: string
  className?: string
}

function roleLabel(role: string) {
  if (!role) return 'Speaker'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

/**
 * Discourse tape demo: auto-play discussion. Coaching lives in the bound agent thread.
 */
export function DiscourseDiscussionPanel({
  sessionId,
  profileId,
  protagonistRole,
  counterpartRole,
  title = 'Discussion',
  subtitle,
  contextLabel,
  className,
}: DiscourseDiscussionPanelProps) {
  const sessionQuery = useDiscourseSession(sessionId)
  useDiscourseSessionSse(sessionId, profileId)
  const appendTurn = useAppendDiscourseTurn(sessionId)
  const simulateTurn = useSimulateDiscourseTurn(sessionId)

  const [playing, setPlaying] = useState(false)
  const [takenOver, setTakenOver] = useState(false)
  const [draft, setDraft] = useState('')
  const [nextSpeaker, setNextSpeaker] = useState<'protagonist' | 'counterpart'>(
    'protagonist',
  )

  const nextSpeakerRef = useRef(nextSpeaker)
  /** Bumped on pause/unmount so in-flight loop iterations and fetches bail out. */
  const playEpochRef = useRef(0)
  const fetchAbortRef = useRef<AbortController | null>(null)
  const simulateMutateRef = useRef(simulateTurn.mutateAsync)
  const advanceSpeakerRef = useRef<(speaker: 'protagonist' | 'counterpart') => void>(
    () => {},
  )

  nextSpeakerRef.current = nextSpeaker
  simulateMutateRef.current = simulateTurn.mutateAsync

  const turns = sessionQuery.data?.turns || []

  const stopPlay = useCallback(() => {
    playEpochRef.current += 1
    fetchAbortRef.current?.abort()
    fetchAbortRef.current = null
    setPlaying(false)
  }, [])

  const takeOver = useCallback(() => {
    stopPlay()
    setTakenOver(true)
  }, [stopPlay])

  const advanceSpeaker = useCallback((speaker: 'protagonist' | 'counterpart') => {
    const next = speaker === 'protagonist' ? 'counterpart' : 'protagonist'
    nextSpeakerRef.current = next
    setNextSpeaker(next)
  }, [])

  advanceSpeakerRef.current = advanceSpeaker

  // Single auto-play loop — epoch + AbortController so Pause stops current + future turns.
  useEffect(() => {
    if (!playing || takenOver) return

    const epoch = playEpochRef.current
    const alive = () => playEpochRef.current === epoch

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        let settled = false
        const finish = () => {
          if (settled) return
          settled = true
          window.clearTimeout(timeoutId)
          window.clearInterval(watchId)
          resolve()
        }
        const timeoutId = window.setTimeout(finish, ms)
        const watchId = window.setInterval(() => {
          if (!alive()) finish()
        }, 100)
      })

    const loop = async () => {
      while (alive()) {
        const speaker = nextSpeakerRef.current
        const ac = new AbortController()
        fetchAbortRef.current = ac
        try {
          await simulateMutateRef.current({ speaker, signal: ac.signal })
        } catch (err) {
          if (!alive() || (err instanceof DOMException && err.name === 'AbortError')) {
            return
          }
          stopPlay()
          return
        } finally {
          if (fetchAbortRef.current === ac) fetchAbortRef.current = null
        }
        if (!alive()) return
        advanceSpeakerRef.current(speaker)
        await sleep(700)
      }
    }

    void loop()
    return () => {
      playEpochRef.current += 1
      fetchAbortRef.current?.abort()
      fetchAbortRef.current = null
    }
  }, [playing, takenOver, stopPlay])

  const onSend = () => {
    const text = draft.trim()
    if (!text || !sessionId) return
    const nextSeq = turns.length + 1
    appendTurn.mutate(
      { sequence: nextSeq, role: protagonistRole, text, source: 'live' },
      {
        onSuccess: () => {
          setDraft('')
          nextSpeakerRef.current = 'counterpart'
          setNextSpeaker('counterpart')
        },
      },
    )
  }

  const onPlay = () => {
    setTakenOver(false)
    playEpochRef.current += 1
    setPlaying(true)
  }

  const onNext = async () => {
    stopPlay()
    const speaker = nextSpeakerRef.current
    const ac = new AbortController()
    fetchAbortRef.current = ac
    try {
      await simulateTurn.mutateAsync({ speaker, signal: ac.signal })
      if (ac.signal.aborted) return
      advanceSpeaker(speaker)
    } catch {
      /* ignore abort / errors */
    } finally {
      if (fetchAbortRef.current === ac) fetchAbortRef.current = null
    }
  }

  return (
    <div className={cn('max-w-3xl', className)}>
      <Card className="flex min-h-[28rem] flex-col">
        <CardHeader className="space-y-2 pb-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              {subtitle ? (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              ) : null}
              {contextLabel ? (
                <p className="mt-1 text-xs text-muted-foreground">{contextLabel}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {!playing ? (
                <Button type="button" size="sm" onClick={onPlay}>
                  <Play className="size-3.5" aria-hidden />
                  Play
                </Button>
              ) : (
                <Button type="button" size="sm" variant="secondary" onClick={stopPlay}>
                  <Pause className="size-3.5" aria-hidden />
                  Pause
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void onNext()}
                disabled={simulateTurn.isPending}
              >
                <SkipForward className="size-3.5" aria-hidden />
                Next
              </Button>
              <Button
                type="button"
                size="sm"
                variant={takenOver ? 'secondary' : 'outline'}
                onClick={takeOver}
              >
                <UserRound className="size-3.5" aria-hidden />
                Take over
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Next auto line: <span className="font-medium">{nextSpeaker}</span>
            {playing ? ' · playing' : takenOver ? ' · you are speaking as protagonist' : ''}
          </p>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="min-h-0 flex-1 space-y-2 overflow-auto rounded-md border border-border bg-muted/20 p-3">
            {sessionQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading transcript…</p>
            ) : turns.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Press Play to start the auto-play discussion, or Take over to type.
              </p>
            ) : (
              turns.map((t) => {
                const isProtagonist = t.role === protagonistRole
                return (
                  <div
                    key={t.id || `${t.sequence}-${t.role}`}
                    className={cn(
                      'rounded-md px-2.5 py-2 text-sm',
                      isProtagonist
                        ? 'ml-4 bg-primary/10'
                        : 'mr-4 border border-border bg-background',
                    )}
                  >
                    <div className="mb-0.5 flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {roleLabel(t.role || (isProtagonist ? protagonistRole : counterpartRole))}
                      </Badge>
                      {t.source === 'demo_sim' ? (
                        <span className="text-[10px] text-muted-foreground">sim</span>
                      ) : null}
                    </div>
                    <p className="whitespace-pre-wrap">{t.text}</p>
                  </div>
                )
              })
            )}
          </div>
          {takenOver ? (
            <div className="flex gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Speak as ${protagonistRole}…`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSend()
                }}
              />
              <Button
                type="button"
                size="sm"
                onClick={onSend}
                disabled={appendTurn.isPending || !draft.trim()}
              >
                Send
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
